import VisitQuotation from '../VisitQuotation';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import PhotoUploader from '../../../components/PhotoUploader';
import { formatDuration, formatTimePanama } from '../utils/agendaDates';
import ExecutionLocationDetails from './ExecutionLocationDetails';

export const OUTCOME_OPTIONS = [
    { label: 'Sin cambios', value: 'noChange' },
    { label: 'Requiere seguimiento', value: 'followUpRequired' },
    { label: 'Solicitó cotización', value: 'quoteRequested' },
    { label: 'Solicitó muestra', value: 'sampleRequested' },
    { label: 'Oportunidad identificada', value: 'opportunityIdentified' },
    { label: 'Pedido potencial', value: 'potentialOrder' },
    { label: 'Queja', value: 'complaint' },
    { label: 'No interesado', value: 'notInterested' },
    { label: 'Visita no completada', value: 'visitNotCompleted' }
];

const emptyForm = {
    prospectName: '',
    areaName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    metWith: '',
    summary: '',
    identifiedNeeds: '',
    productsOfInterest: '',
    objections: '',
    agreements: '',
    nextSteps: '',
    followUpDate: '',
    outcome: '',
    additionalComments: '',
    prospectsDiscovered: '',
    photos: []
};

const photosFromReport = (report) => (Array.isArray(report?.photos) ? report.photos : [])
    .map((photo) => (typeof photo === 'string' ? photo : photo?.url))
    .filter(Boolean);

const normalizeForm = (visit) => {
    const report = visit?.report || {};
    return {
        prospectName: visit?.prospectName || '',
        areaName: visit?.areaName || '',
        contactName: visit?.contactName || '',
        contactPhone: visit?.contactPhone || '',
        contactEmail: visit?.contactEmail || '',
        address: visit?.address || '',
        metWith: report.metWith || '',
        summary: report.summary || '',
        identifiedNeeds: report.identifiedNeeds || '',
        productsOfInterest: report.productsOfInterest || '',
        objections: report.objections || '',
        agreements: report.agreements || '',
        nextSteps: report.nextSteps || '',
        followUpDate: report.followUpDate || '',
        outcome: report.outcome || '',
        additionalComments: report.additionalComments || '',
        prospectsDiscovered: report.prospectsDiscovered || '',
        photos: photosFromReport(report)
    };
};

const trimOrNull = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
};

const buildPayload = (form) => ({
    metWith: trimOrNull(form.metWith),
    summary: trimOrNull(form.summary),
    identifiedNeeds: trimOrNull(form.identifiedNeeds),
    productsOfInterest: trimOrNull(form.productsOfInterest),
    objections: trimOrNull(form.objections),
    agreements: trimOrNull(form.agreements),
    nextSteps: trimOrNull(form.nextSteps),
    followUpDate: trimOrNull(form.followUpDate),
    outcome: trimOrNull(form.outcome),
    additionalComments: trimOrNull(form.additionalComments),
    prospectsDiscovered: trimOrNull(form.prospectsDiscovered),
    photos: form.photos || []
});

const buildLivePayload = (form, visit) => ({
    ...buildPayload(form),
    ...(visit?.visitType === 'prospect'
        ? { prospectName: trimOrNull(form.prospectName) }
        : {}),
    ...(visit?.visitType === 'areaProspecting'
        ? { areaName: trimOrNull(form.areaName) }
        : {
            contactName: trimOrNull(form.contactName),
            contactPhone: trimOrNull(form.contactPhone),
            contactEmail: trimOrNull(form.contactEmail),
            address: trimOrNull(form.address)
        })
});

const ReportVisitDialog = ({
    visible,
    visit,
    readOnly = false,
    locked = false,
    loading = false,
    settings,
    onHide,
    onSaveDraft,
    onSubmitReport,
    onLiveSave
}) => {
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [dirty, setDirty] = useState(false);
    const [saveState, setSaveState] = useState('idle');
    const liveMode = visit?.executionStatus === 'inProgress';
    const recoveryKey = visit?._id ? `sales-live-meeting:${visit._id}` : '';

    useEffect(() => {
        if (!visible) {
            setForm(emptyForm);
            setErrors({});
            setDirty(false);
            setSaveState('idle');
            return;
        }
        const serverForm = normalizeForm(visit);
        if (liveMode && recoveryKey) {
            try {
                const recovered = JSON.parse(localStorage.getItem(recoveryKey) || 'null');
                if (recovered?.form) {
                    setForm({ ...serverForm, ...recovered.form });
                    setDirty(true);
                    setSaveState('recovered');
                } else {
                    setForm(serverForm);
                    setDirty(false);
                    setSaveState('idle');
                }
            } catch {
                setForm(serverForm);
                setDirty(false);
                setSaveState('idle');
            }
        } else {
            setForm(serverForm);
            setDirty(false);
            setSaveState('idle');
        }
        setErrors({});
    }, [liveMode, recoveryKey, visible, visit]);

    const reportStatus = visit?.reportStatus || (visit?.executionStatus === 'completed' ? 'submitted' : 'notStarted');
    const reportSettings = settings?.reports || {};
    const maxReportPhotos = Number(reportSettings.maxPhotos ?? 10);
    const maxReportPhotoBytes = Number(reportSettings.maxPhotoBytes ?? 750 * 1024);
    const requiredFields = reportSettings.requiredFields || ['summary', 'outcome'];
    const followUpRequired = (reportSettings.followUpRequiredOutcomes || []).includes(form.outcome);
    const canEdit = !readOnly && !locked;
    const title = useMemo(() => {
        if (liveMode && canEdit) return 'Reunión en progreso';
        if (readOnly || locked) return 'Ver reporte';
        if (reportStatus === 'submitted') return 'Editar reporte';
        if (reportStatus === 'draft') return 'Continuar reporte';
        return 'Completar reporte';
    }, [canEdit, liveMode, locked, readOnly, reportStatus]);

    useEffect(() => {
        if (!visible || !liveMode || !canEdit || !dirty || !recoveryKey) return undefined;
        try {
            const recoverableForm = Object.fromEntries(
                Object.entries(form).filter(([field]) => field !== 'photos')
            );
            localStorage.setItem(recoveryKey, JSON.stringify({
                form: recoverableForm,
                savedLocallyAt: new Date().toISOString()
            }));
        } catch {
            setSaveState('local');
        }
        const timer = window.setTimeout(async () => {
            if (!onLiveSave) return;
            setSaveState('saving');
            const saved = await onLiveSave(
                { ...buildLivePayload(form, visit), autoSave: true },
                { silent: true }
            );
            if (saved) {
                localStorage.removeItem(recoveryKey);
                setDirty(false);
                setSaveState('saved');
            } else {
                setSaveState('local');
            }
        }, 8000);
        return () => window.clearTimeout(timer);
    }, [canEdit, dirty, form, liveMode, onLiveSave, recoveryKey, visible, visit]);

    const validateSubmit = () => {
        const nextErrors = {};
        if (requiredFields.includes('summary') && !trimOrNull(form.summary)) nextErrors.summary = 'El resumen es obligatorio.';
        if (requiredFields.includes('outcome') && !trimOrNull(form.outcome)) nextErrors.outcome = 'Selecciona un resultado.';
        if (requiredFields.includes('metWith') && !trimOrNull(form.metWith)) nextErrors.metWith = 'Indica la persona con la que se reunió.';
        if (followUpRequired && !trimOrNull(form.nextSteps)) nextErrors.nextSteps = 'Indica los próximos pasos.';
        if (followUpRequired && !trimOrNull(form.followUpDate)) nextErrors.followUpDate = 'Indica una fecha de seguimiento.';
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: null }));
        if (liveMode) {
            setDirty(true);
            setSaveState('local');
        }
    };

    const addPhoto = (photo) => {
        if (!photo) return;
        if ((form.photos || []).length >= maxReportPhotos) {
            toast.warn(`Máximo ${maxReportPhotos} fotos permitidas.`);
            return;
        }
        setForm((prev) => ({ ...prev, photos: [...(prev.photos || []), photo] }));
        if (liveMode) {
            setDirty(true);
            setSaveState('local');
        }
    };

    const saveLiveNow = async () => {
        if (!onLiveSave) return false;
        setSaveState('saving');
        const saved = await onLiveSave(
            { ...buildLivePayload(form, visit), autoSave: false },
            { silent: false }
        );
        if (saved) {
            if (recoveryKey) localStorage.removeItem(recoveryKey);
            setDirty(false);
            setSaveState('saved');
            return true;
        } else {
            setSaveState('local');
            return false;
        }
    };

    const handleDialogHide = async () => {
        if (liveMode && canEdit && dirty) {
            const saved = await saveLiveNow();
            if (!saved) return;
        }
        onHide();
    };

    const footer = (
        <div className="flex flex-column sm:flex-row gap-2 justify-content-end">
            <Button type="button" label={liveMode || !canEdit ? 'Cerrar' : 'Cancelar'} className="p-button-text" onClick={handleDialogHide} disabled={loading} />
            {canEdit ? (
                liveMode ? (
                    <Button
                        type="button"
                        label={saveState === 'saving' ? 'Guardando…' : 'Guardar ahora'}
                        icon="pi pi-save"
                        onClick={saveLiveNow}
                        loading={loading || saveState === 'saving'}
                        disabled={loading || saveState === 'saving'}
                    />
                ) : <>
                    <Button
                        type="button"
                        label="Guardar borrador"
                        icon="pi pi-save"
                        outlined
                        onClick={() => onSaveDraft(buildPayload(form))}
                        loading={loading}
                        disabled={loading}
                    />
                    <Button
                        type="button"
                        label="Enviar reporte"
                        icon="pi pi-check"
                        onClick={() => {
                            if (validateSubmit()) onSubmitReport(buildPayload(form));
                        }}
                        loading={loading}
                        disabled={loading}
                    />
                </>
            ) : null}
        </div>
    );

    return (
        <Dialog
            header={title}
            visible={visible}
            modal
            className="ventas-dialog ventas-report-dialog"
            style={{ width: 'min(94vw, 760px)' }}
            breakpoints={{ '768px': '96vw' }}
            onHide={handleDialogHide}
            footer={footer}
        >
            <div className="flex align-items-center gap-2 flex-wrap mb-3">
                <Tag value={reportStatus === 'submitted' ? 'Reporte enviado' : reportStatus === 'draft' ? 'Borrador' : 'No iniciado'} severity={reportStatus === 'submitted' ? 'success' : 'warning'} />
                {liveMode ? <Tag value="En vivo" severity="info" /> : null}
                {liveMode && saveState === 'saved' ? <Tag value="Guardado" icon="pi pi-check" severity="success" /> : null}
                {liveMode && saveState === 'local' ? <Tag value="Pendiente de sincronizar" icon="pi pi-cloud-upload" severity="warning" /> : null}
                {liveMode && saveState === 'recovered' ? <Tag value="Borrador recuperado" icon="pi pi-history" severity="warning" /> : null}
                {locked ? <Tag value="Semana cerrada" severity="secondary" /> : null}
                {readOnly ? <Tag value="Solo lectura" severity="info" /> : null}
            </div>

            {locked ? (
                <div className="surface-100 border-round p-3 mb-3 text-color-secondary">
                    Semana cerrada · Reportes e imágenes en modo de solo lectura
                </div>
            ) : null}

            {liveMode && canEdit ? (
                <div className="ventas-live-meeting-banner mb-3">
                    <i className="pi pi-cloud-upload" aria-hidden="true" />
                    <div>
                        <strong>Puedes escribir durante la reunión.</strong>
                        <span>Los cambios se guardan automáticamente después de 8 segundos sin escribir. El reporte no se enviará hasta finalizar la visita.</span>
                    </div>
                </div>
            ) : null}

            <div className="surface-50 border-round p-3 mb-3">
                <div className="font-medium mb-2">Detalles de ejecución</div>
                <div className="text-sm text-color-secondary">
                    Inicio: {visit?.startedAt ? formatTimePanama(visit.startedAt) : 'Sin registrar'}
                </div>
                <VisitQuotation visit={visit} />
                <ExecutionLocationDetails label="Ubicación de inicio" location={visit?.startLocation} />
                <div className="text-sm text-color-secondary mt-2">
                    Finalización: {visit?.endedAt ? formatTimePanama(visit.endedAt) : 'Sin registrar'}
                    {visit?.durationSeconds !== null && visit?.durationSeconds !== undefined
                        ? ` · duración ${formatDuration(visit.durationSeconds)}`
                        : ''}
                </div>
                {visit?.endedAt ? (
                    <ExecutionLocationDetails label="Ubicación de finalización" location={visit?.endLocation} />
                ) : null}
            </div>

            <div className="grid p-fluid">
                {liveMode ? (
                    <>
                        <div className="col-12">
                            <h4 className="mt-0 mb-1">Datos encontrados en la reunión</h4>
                            <small className="text-color-secondary">
                                {visit?.visitType === 'existingClient'
                                    ? 'El cliente de QuickBooks no cambia; estos datos se guardan en la visita.'
                                    : visit?.visitType === 'areaProspecting'
                                        ? 'Actualiza la zona y registra los prospectos encontrados mientras realizas el recorrido.'
                                        : 'Completa el prospecto a medida que obtienes la información.'}
                            </small>
                        </div>
                        {visit?.visitType === 'prospect' ? (
                            <div className="col-12">
                                <label className="block font-medium mb-2">Empresa o prospecto</label>
                                <InputText value={form.prospectName} onChange={(event) => updateField('prospectName', event.target.value)} disabled={!canEdit} maxLength={180} />
                            </div>
                        ) : null}
                        {visit?.visitType === 'areaProspecting' ? (
                            <div className="col-12">
                                <label className="block font-medium mb-2">Zona, ruta o sector</label>
                                <InputText value={form.areaName} onChange={(event) => updateField('areaName', event.target.value)} disabled={!canEdit} maxLength={240} />
                            </div>
                        ) : (
                            <>
                                <div className="col-12 md:col-6">
                                    <label className="block font-medium mb-2">Persona de contacto</label>
                                    <InputText value={form.contactName} onChange={(event) => updateField('contactName', event.target.value)} disabled={!canEdit} maxLength={180} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="block font-medium mb-2">Teléfono</label>
                                    <InputText value={form.contactPhone} onChange={(event) => updateField('contactPhone', event.target.value)} disabled={!canEdit} maxLength={80} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="block font-medium mb-2">Correo electrónico</label>
                                    <InputText type="email" value={form.contactEmail} onChange={(event) => updateField('contactEmail', event.target.value)} disabled={!canEdit} maxLength={254} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="block font-medium mb-2">Dirección</label>
                                    <InputText value={form.address} onChange={(event) => updateField('address', event.target.value)} disabled={!canEdit} maxLength={500} />
                                </div>
                            </>
                        )}
                        <div className="col-12"><hr className="ventas-live-meeting-divider" /></div>
                    </>
                ) : null}
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">{visit?.visitType === 'areaProspecting' ? 'Contacto principal encontrado (si aplica)' : 'Persona con la que se reunió'}</label>
                    <InputText value={form.metWith} onChange={(event) => updateField('metWith', event.target.value)} disabled={!canEdit} maxLength={180} />
                    {errors.metWith ? <small className="p-error">{errors.metWith}</small> : null}
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Resultado</label>
                    <Dropdown value={form.outcome} options={OUTCOME_OPTIONS} onChange={(event) => updateField('outcome', event.value)} placeholder="Selecciona resultado" disabled={!canEdit} />
                    {errors.outcome ? <small className="p-error">{errors.outcome}</small> : null}
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">{visit?.visitType === 'areaProspecting' ? 'Resumen del recorrido' : 'Resumen de la visita'}</label>
                    <InputTextarea value={form.summary} onChange={(event) => updateField('summary', event.target.value)} rows={4} autoResize disabled={!canEdit} maxLength={4000} />
                    {errors.summary ? <small className="p-error">{errors.summary}</small> : null}
                </div>
                {visit?.visitType === 'areaProspecting' ? (
                    <div className="col-12">
                        <label className="block font-medium mb-2">Prospectos y oportunidades encontradas</label>
                        <InputTextarea
                            value={form.prospectsDiscovered}
                            onChange={(event) => updateField('prospectsDiscovered', event.target.value)}
                            rows={4}
                            autoResize
                            disabled={!canEdit}
                            maxLength={4000}
                            placeholder="Anota nombre del comercio, ubicación, contacto y oportunidad observada."
                        />
                    </div>
                ) : null}
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Necesidades identificadas</label>
                    <InputTextarea value={form.identifiedNeeds} onChange={(event) => updateField('identifiedNeeds', event.target.value)} rows={3} autoResize disabled={!canEdit} maxLength={3000} />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Productos de interés</label>
                    <InputTextarea value={form.productsOfInterest} onChange={(event) => updateField('productsOfInterest', event.target.value)} rows={3} autoResize disabled={!canEdit} maxLength={2000} />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Objeciones o problemas</label>
                    <InputTextarea value={form.objections} onChange={(event) => updateField('objections', event.target.value)} rows={3} autoResize disabled={!canEdit} maxLength={2000} />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Acuerdos</label>
                    <InputTextarea value={form.agreements} onChange={(event) => updateField('agreements', event.target.value)} rows={3} autoResize disabled={!canEdit} maxLength={3000} />
                </div>
                <div className="col-12 md:col-8">
                    <label className="block font-medium mb-2">Próximos pasos</label>
                    <InputTextarea value={form.nextSteps} onChange={(event) => updateField('nextSteps', event.target.value)} rows={3} autoResize disabled={!canEdit} maxLength={3000} />
                    {errors.nextSteps ? <small className="p-error">{errors.nextSteps}</small> : null}
                </div>
                <div className="col-12 md:col-4">
                    <label className="block font-medium mb-2">Fecha de seguimiento</label>
                    <InputText type="date" value={form.followUpDate} onChange={(event) => updateField('followUpDate', event.target.value)} disabled={!canEdit} />
                    {errors.followUpDate ? <small className="p-error">{errors.followUpDate}</small> : null}
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">Comentarios adicionales</label>
                    <InputTextarea value={form.additionalComments} onChange={(event) => updateField('additionalComments', event.target.value)} rows={3} autoResize disabled={!canEdit} maxLength={3000} />
                </div>
                <div className="col-12">
                    {canEdit && maxReportPhotos > 0 ? (
                        <PhotoUploader
                            label="Imágenes"
                            value=""
                            onChange={addPhoto}
                            helperText={`Opcional. Hasta ${maxReportPhotos} fotos. Máximo ${Math.round(maxReportPhotoBytes / 1024)} KB cada una después de compresión.`}
                            maxUploadBytes={maxReportPhotoBytes}
                            maxDimension={1280}
                            disabled={loading}
                        />
                    ) : canEdit ? (
                        <label className="block font-medium mb-2">Imágenes deshabilitadas por configuración</label>
                    ) : (
                        <label className="block font-medium mb-2">Imágenes</label>
                    )}
                    {(form.photos || []).length ? (
                        <div className="grid mt-2">
                            {form.photos.map((photo, index) => (
                                <div className="col-6 md:col-3" key={`${photo}-${index}`}>
                                    <div className="ventas-report-photo">
                                        <img src={photo} alt={`Evidencia ${index + 1}`} onClick={() => window.open(photo, '_blank', 'noopener,noreferrer')} />
                                        {canEdit ? (
                                            <Button
                                                type="button"
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-danger p-button-sm ventas-report-photo-remove"
                                                onClick={() => {
                                                    setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, photoIndex) => photoIndex !== index) }));
                                                    if (liveMode) {
                                                        setDirty(true);
                                                        setSaveState('local');
                                                    }
                                                }}
                                                aria-label="Eliminar foto"
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <small className="text-color-secondary block mt-2">Sin imágenes adjuntas.</small>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default ReportVisitDialog;
